/**
 * advance-ship-commands.ts — v3M22 per-tick player-command pass.
 *
 * Translates `ShipInstance.commandKind` ('patrol' / 'guard') into
 * concrete `moveOrder` writes that the existing `advanceShipMovement`
 * loop can step toward. Runs BEFORE `advanceShipMovement` in the
 * advanceTick pipeline so any moveOrder set here lands cleanly in the
 * same tick.
 *
 * Pass design (`MECHANICS.md §5`):
 *
 *   - **Patrol** (§5.2): cycles the `waypoints` queue. When the ship's
 *     `moveOrder` is null AND `waypoints.length > 0`, the head waypoint
 *     becomes the new `moveOrder` and the queue rotates (head moves to
 *     tail). Empty waypoints → no-op. `moveOrder` non-null → no-op
 *     (the ship is still en route to the previous waypoint, movement
 *     pass clears it on arrival).
 *
 *   - **Guard planet** (§5.4): orbits the planet at
 *     `planetSimRadius + GUARD_PLANET_ORBIT_GAP`. Each tick the ship's
 *     current angle around the planet centre advances by
 *     `GUARD_ORBIT_ANGULAR_STEP` and the new target point becomes the
 *     `moveOrder`. The ship chases the orbit point — if it can keep up
 *     (sublight ≥ orbit-arc-per-tick), the motion looks smooth; if not,
 *     it lags but still circles.
 *
 *   - **Guard ship** (§5.4): follows a friendly target ship at a small
 *     leash. `moveOrder` is recomputed every tick from the target's
 *     current `(sx, sy)` plus a fixed offset. Cancels the guard if the
 *     target leaves the ship's system or is destroyed.
 *
 *   - **Explore** (§5.3): NOT YET IMPLEMENTED — lands in v3M22.3 with
 *     the auto-jump destination picker. The pass currently no-ops on
 *     `'explore'`; the field is set so the UI can already reflect the
 *     intent.
 *
 *   - **Idle**: skipped entirely.
 *
 * Cancel-on-jump (§5.5) is enforced by the jump-start sites (the
 * `tryStartPendingJump` family in `advance.ts` + `applyJumpShips` in
 * `commands.ts`) calling `clearShipCommandFields` when the ship enters
 * hyperspace. The pass itself doesn't have to defend against
 * mid-jump ships; they're already filtered out by the systemId-null
 * guard at the top of each kind's branch.
 */

import { planetSimRadiusForSize } from './balance-bombardment';
import { applyCommand, clearShipCommandFields } from './commands';
import type { Empire, GameState, ShipInstance, SystemId } from '../state/types';

/**
 * Gap between the planet's outer sim radius and the guard-planet orbit
 * ring. 0.04 in-coord units puts the orbit just outside the planet's
 * footprint — close enough to read as "guarding" without clipping the
 * planet glyph at any zoom level.
 */
const GUARD_PLANET_ORBIT_GAP = 0.04;

/**
 * Angular advance per tick on the guard-planet orbit. 0.04 rad/tick @
 * 10 Hz = ~23°/sec → full orbit in ~16 seconds. Tuned to feel
 * "patrolling" rather than racing; revisit in v3M11 polish if
 * playtest signal flags it.
 */
const GUARD_ORBIT_ANGULAR_STEP = 0.04;

/**
 * Leash offset for guard-ship — the guarding ship parks at this
 * `(dx, dy)` relative to the target's `(sx, sy)`. v3M22.2 uses a fixed
 * offset; multi-guard formations (multiple ships escorting one target,
 * spread around it) are deferred to v3M11 polish.
 */
const GUARD_SHIP_LEASH_DX = -0.05;
const GUARD_SHIP_LEASH_DY = 0;

/** Advance ship-commands by exactly one tick. Mutates state. */
export function advanceShipCommands(state: GameState): void {
  for (const ship of state.ships.values()) {
    if (ship.commandKind === 'idle') continue;
    if (ship.systemId === null) continue; // mid-jump — wait for arrival
    if (ship.disabledTicks > 0) continue; // EMP — same handling as movement

    if (ship.commandKind === 'patrol') {
      runPatrol(ship);
    } else if (ship.commandKind === 'guard') {
      runGuard(state, ship);
    } else if (ship.commandKind === 'explore') {
      runExplore(state, ship);
    }
  }
}

/**
 * Patrol: cycle the waypoint queue. When `moveOrder` is null (ship
 * just arrived at the previous waypoint, or the patrol was just
 * issued), pop the head off `waypoints`, set it as the new
 * `moveOrder`, and rotate the popped value to the tail of the queue
 * so the next arrival picks up the next-in-line.
 *
 * Empty queue → no-op (the player must place at least one waypoint).
 */
function runPatrol(ship: ShipInstance): void {
  if (ship.moveOrder !== null) return; // still en route to current target
  if (ship.waypoints.length === 0) return; // no waypoints to follow
  const head = ship.waypoints[0]!;
  ship.moveOrder = { targetX: head.targetX, targetY: head.targetY };
  // Rotate: head moves to the tail. For a 1-waypoint patrol this is
  // a no-op (the queue stays [head]); the ship will arrive, clear
  // moveOrder, then re-target the same point next tick — effectively
  // hovering at it. Probably not what the player intended for a single
  // waypoint, but we don't second-guess the queue shape.
  ship.waypoints = [...ship.waypoints.slice(1), head];
}

/**
 * Guard: dispatch on `guardTargetKind`. Validates target existence
 * + same-system-and-alive; if the target has vanished or moved out of
 * the guarding ship's system, the guard auto-cancels (clears all
 * v3M22 command fields).
 */
function runGuard(state: GameState, ship: ShipInstance): void {
  if (ship.guardTargetKind === 'planet') {
    runGuardPlanet(state, ship);
  } else if (ship.guardTargetKind === 'ship') {
    runGuardShip(state, ship);
  } else {
    // Defensive — `commandKind === 'guard'` with no target kind is an
    // inconsistent state. Clear and move on.
    clearShipCommandFields(ship);
  }
}

/**
 * Guard a planet: orbit at `planetSimRadius + GUARD_PLANET_ORBIT_GAP`.
 * Each tick advances the ship's current angle around the planet
 * centre by `GUARD_ORBIT_ANGULAR_STEP` and writes a new `moveOrder`
 * at the next orbit point. The ship chases the moving target point
 * — visually orbits the planet.
 */
function runGuardPlanet(state: GameState, ship: ShipInstance): void {
  if (ship.guardTargetId === null) {
    clearShipCommandFields(ship);
    return;
  }
  const planet = state.planets.get(ship.guardTargetId);
  if (!planet || planet.systemId !== ship.systemId) {
    clearShipCommandFields(ship);
    return;
  }
  const radius = planetSimRadiusForSize(planet.size) + GUARD_PLANET_ORBIT_GAP;
  const dx = ship.sx - planet.systemX;
  const dy = ship.sy - planet.systemY;
  const currentAngle = Math.atan2(dy, dx);
  const nextAngle = currentAngle + GUARD_ORBIT_ANGULAR_STEP;
  ship.moveOrder = {
    targetX: planet.systemX + Math.cos(nextAngle) * radius,
    targetY: planet.systemY + Math.sin(nextAngle) * radius,
  };
}

/**
 * Guard a friendly ship: follow at a fixed `(dx, dy)` leash. Cancels
 * if the target despawns, leaves the system, or enters hyperspace.
 */
function runGuardShip(state: GameState, ship: ShipInstance): void {
  if (ship.guardTargetId === null) {
    clearShipCommandFields(ship);
    return;
  }
  const target = state.ships.get(ship.guardTargetId);
  if (!target || target.systemId !== ship.systemId || target.currentHull <= 0) {
    clearShipCommandFields(ship);
    return;
  }
  ship.moveOrder = {
    targetX: target.sx + GUARD_SHIP_LEASH_DX,
    targetY: target.sy + GUARD_SHIP_LEASH_DY,
  };
}

/**
 * v3M22.3 — Explore: auto-jump to the least-scouted reachable system,
 * then on arrival the next tick picks the next-least-scouted, until
 * every reachable system has been scouted at which point the command
 * auto-clears.
 *
 * The pass is idempotent within a tick — it only fires the
 * sail-then-jump dispatch when the ship is fully idle (no `moveOrder`,
 * no `pendingJumpSystemId`, not mid-jump). While the ship is en route
 * to the perimeter or in hyperspace, the pass no-ops.
 *
 * `commandKind === 'explore'` survives jump-start (see
 * `cancelShipCommandOnJump` in commands.ts) so the chain continues
 * across jumps without the player having to re-issue.
 *
 * Exhaustion: when no eligible destination remains (all reachable
 * systems are in `Empire.scoutedSystemIds`), `clearShipCommandFields`
 * resets the ship to `'idle'` and the explore is over.
 */
function runExplore(state: GameState, ship: ShipInstance): void {
  // Already en route or in transit — nothing to do this tick.
  if (ship.moveOrder !== null) return;
  if (ship.pendingJumpSystemId !== null) return;
  if (ship.pendingWarpGateLinkId !== null) return;

  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) {
    clearShipCommandFields(ship);
    return;
  }
  const target = pickExploreDestination(state, empire, ship);
  if (target === null) {
    // No unscouted reachable system from here — explore exhausted.
    clearShipCommandFields(ship);
    return;
  }
  // Issue the standard sail-then-jump command. The ship moves to the
  // JP first, then auto-fires the hyperdrive on arrival via
  // `tryStartPendingJump`. `commandKind === 'explore'` is preserved by
  // `cancelShipCommandOnJump` so the next tick after arrival runs
  // explore again.
  applyCommand(state, {
    kind: 'moveAndJumpShips',
    shipIds: [ship.id],
    targetSystemId: target.destSystemId,
    arrivalAngle: null,
  });
}

/**
 * v3M22.3 — pick the explore destination from the ship's current
 * system. Walks every linked jump point and prefers destinations the
 * empire has not yet scouted (lowest `scoutedSystemIds` priority,
 * effectively the inversion of `scoutedSystemIds`-membership). Among
 * unscouted destinations, ties break on ascending `destSystemId` for
 * determinism. If every linked destination is already scouted,
 * returns null (caller auto-clears the explore).
 *
 * Mirrors `pickScoutJumpDestination` in `advance-ai.ts` but uses
 * `Empire.scoutedSystemIds` (one-shot persistent scout flag from
 * MFix6.3) rather than visible-ship counts in `teamVision`. The
 * player's intent on Explore is "discover the map" — once a system
 * has been visited it stays explored regardless of whether they
 * currently have eyes there.
 */
function pickExploreDestination(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
): { destSystemId: SystemId } | null {
  if (ship.systemId === null) return null;
  const system = state.systems.get(ship.systemId);
  if (!system) return null;

  let best: { destSystemId: SystemId } | null = null;
  for (const destId of system.adjacentSystemIds) {
    // Skip already-scouted destinations.
    if (empire.scoutedSystemIds.has(destId)) continue;
    // Tiebreak: ascending destSystemId for determinism.
    if (best === null || destId < best.destSystemId) {
      best = { destSystemId: destId };
    }
  }
  return best;
}
